import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useSearch, useNavigate } from '@tanstack/react-router'
import { X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../components/layout/PageHeader'
import { Tabs, type TabDef, MultiSelect, Button } from '../../components/ui'
import { useTheses } from './thesesData'
import {
  useTradesList,
  useFillsList,
  useScriptRunsList,
  useScriptRun,
  useDailyPnL,
  useExposure,
  useExposureIntervals,
  useTraderScripts,
  useTraderScript,
} from './useTrades'
import type {
  ApiTradeRow,
  ApiFillRow,
  ApiScriptRunRow,
  ApiDailyPnLRow,
  ApiDailyPnLGroup,
  ApiDailyPnLLeg,
  ApiExposureLeg,
  ApiExposureGroup,
  ApiIntervalExposureRow,
  ApiIntervalExposureLeg,
  ApiTraderScript,
} from '../../lib/tradesApi'
import {
  patchTraderScript,
  deleteTraderScript,
  createTraderScript,
} from '../../lib/tradesApi'

type Tab = 'positions' | 'executions' | 'pnl' | 'runs' | 'scripts' | 'old-orders' | 'old-exposure' | 'old-pnl'
type TabSearch = Tab | 'orders' | 'fills'

const TRADE_TABS: TabDef<Tab>[] = [
  { key: 'positions', label: 'Positions' },
  { key: 'executions', label: 'Executions' },
  { key: 'pnl', label: 'P&L' },
  { key: 'runs', label: 'Runs' },
  { key: 'scripts', label: 'Scripts' },
  { key: 'old-orders', label: 'Old Intents' },
  { key: 'old-exposure', label: 'Old Exposure' },
  { key: 'old-pnl', label: 'Old P&L' },
]

interface TradesSearch {
  tab?: TabSearch
  thesis?: string[]
  product?: string[]
  status?: string[]
  contract?: string
  from?: string
  to?: string
  errored_only?: boolean
  enabled_only?: boolean
  graduated_only?: boolean
  view?: string
}

const PRODUCTS = ['rdn', 'rdbee', 'rtpe'] as const
const TABLE_PAGE_SIZE = 100
const ORDER_STATUSES = ['open', 'filled', 'partial', 'cancelled', 'expired', 'unfilled'] as const
const EXPOSURE_STATUSES = [
  'FILLED FLAT',
  'PENDING FLAT',
  'OPEN LONG',
  'OPEN SHORT',
  'PARTIAL LONG',
  'PARTIAL SHORT',
  'UNCLOSEABLE LONG',
  'UNCLOSEABLE SHORT',
] as const
const PAPER_MARKET_DELAY_MS = 24 * 60 * 60 * 1000
const FIELD_SEPARATOR = ', '
const LIST_SEPARATOR = ' | '

function normalizeTab(tab?: TabSearch): Tab {
  if (tab === 'orders') return 'old-orders'
  if (tab === 'fills') return 'old-pnl'
  if (
    tab === 'positions' ||
    tab === 'executions' ||
    tab === 'pnl' ||
    tab === 'runs' ||
    tab === 'scripts' ||
    tab === 'old-orders' ||
    tab === 'old-exposure' ||
    tab === 'old-pnl'
  ) {
    return tab
  }
  return 'positions'
}

function defaultFrom(): string {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return d.toISOString()
}

function ensureArray<T>(v: T | T[] | undefined): T[] {
  if (v == null) return []
  return Array.isArray(v) ? v : [v]
}

function fmtTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function fmtPrice(v: number): string {
  return v.toFixed(2)
}

function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso || '—'
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function fmtDateShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso || '—'
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
  })
}

function tradeDateMeta(order: {
  realistic_trade_date?: string | null
  delivery_date?: string | null
  paper_trade_date?: string | null
}): string | null {
  const parts: string[] = []
  if (order.realistic_trade_date) parts.push(`market ${fmtDate(order.realistic_trade_date)}`)
  if (order.delivery_date) parts.push(`delivery ${fmtDate(order.delivery_date)}`)
  if (order.paper_trade_date) parts.push(`paper ${fmtDate(order.paper_trade_date)}`)
  return parts.length > 0 ? parts.join(FIELD_SEPARATOR) : null
}

function fmtDateTimeShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso || '—'
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function addMinutesIso(iso: string, minutes: number): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Date(d.getTime() + minutes * 60 * 1000).toISOString()
}

function fmtDeliveryWindow(first: string, last: string): string {
  return `${fmtDateTimeShort(first)} → ${fmtDateTimeShort(addMinutesIso(last, 14))}`
}

function fmtDateTimeRange(first: string, last: string): string {
  if (!last || first === last) return fmtDateTimeShort(first)
  return `${fmtDateTimeShort(first)} → ${fmtDateTimeShort(last)}`
}

function formatSignedMoney(v: number): string {
  return `${v > 0 ? '+' : ''}${fmtPrice(v)} PLN`
}

function formatOptionalMoney(v: number | null): string {
  return v == null ? '—' : formatSignedMoney(v)
}

function formatSignedMWh(v: number): string {
  return `${v > 0 ? '+' : ''}${fmtPrice(v)}`
}

function pnlText(v: number): string {
  if (v > 0) return 'text-emerald-700'
  if (v < 0) return 'text-red-700'
  return 'text-gray-600'
}

function optionalPnlText(v: number | null): string {
  return v == null ? 'text-gray-400' : pnlText(v)
}

function productLabel(product: string): string {
  if (product === 'rdbee') return 'RDBEE'
  if (product === 'rtpe') return 'RTPE'
  if (product === 'rdn') return 'RDN'
  return product.toUpperCase()
}

type RdnContractParts = {
  date: string
  hour: string
  fixing: '1' | '2'
}

function parseRdnContractCode(contract: string): RdnContractParts | null {
  const m = contract.match(/^(\d{4}-\d{2}-\d{2})_H(\d{2})_F([12])$/)
  if (!m) return null
  return { date: m[1], hour: `H${m[2]}`, fixing: m[3] as '1' | '2' }
}

function rdnFixingLabel(fixing: '1' | '2'): string {
  return `F${fixing}`
}

function fmtTimeOnly(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso || '—'
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

type RdbeeContractParts = {
  startIso: string
  endIso: string
  durationMinutes: number
}

function parseRdbeeContractCode(contract: string): RdbeeContractParts | null {
  const m = contract.match(/^(\d{4})(\d{2})(\d{2}) (\d{2}:\d{2})-(\d{4})(\d{2})(\d{2}) (\d{2}:\d{2})$/)
  if (!m) return null
  const startIso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:00`
  const endIso = `${m[5]}-${m[6]}-${m[7]}T${m[8]}:00`
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  const durationMinutes = Number.isFinite(start) && Number.isFinite(end) ? Math.round((end - start) / 60000) : 0
  return { startIso, endIso, durationMinutes }
}

function rdbeeGranularityLabel(durationMinutes: number): string {
  if (durationMinutes === 60) return '1h'
  if (durationMinutes > 0 && durationMinutes % 60 === 0) return `${durationMinutes / 60}h`
  if (durationMinutes > 0) return `${durationMinutes}m`
  return ''
}

function rdbeeInstrumentLabel(contract: string): string | null {
  const parsed = parseRdbeeContractCode(contract)
  if (!parsed) return null
  const granularity = rdbeeGranularityLabel(parsed.durationMinutes)
  const product = granularity ? `RDBEE ${granularity}` : 'RDBEE'
  return `${product}${FIELD_SEPARATOR}${fmtDateShort(parsed.startIso)}${FIELD_SEPARATOR}${fmtTimeOnly(parsed.startIso)}-${fmtTimeOnly(addMinutesIso(parsed.endIso, -1))}`
}

function instrumentLabel(product: string, contract: string): string {
  if (product === 'rdn') {
    const parsed = parseRdnContractCode(contract)
    if (parsed) return `${rdnFixingLabel(parsed.fixing)}${FIELD_SEPARATOR}${fmtDateShort(parsed.date)}${FIELD_SEPARATOR}${parsed.hour}`
  }
  if (product === 'rdbee') {
    return rdbeeInstrumentLabel(contract) ?? contract
  }
  if (product === 'rtpe') {
    const [profile, period] = contract.split('_', 2)
    if (profile && period) return `RTPE ${profile}${FIELD_SEPARATOR}${period}`
  }
  return contract
}

function exposureInstrumentLabel(markets: string[], instruments: string[], limit = 3): string {
  if (instruments.length === 0) return '—'
  if (instruments.length > limit) return `${instruments.length} various instruments`
  const isRdnOnly = markets.length === 1 && markets[0] === 'rdn'
  if (isRdnOnly) {
    const parsed = instruments.map(parseRdnContractCode)
    if (parsed.every(Boolean)) {
      const first = parsed[0] as RdnContractParts
      const sameHour = parsed.every((item) => item?.date === first.date && item?.hour === first.hour)
      if (sameHour) {
        const fixings = parsed
          .map((item) => rdnFixingLabel((item as RdnContractParts).fixing))
          .filter((value, idx, arr) => arr.indexOf(value) === idx)
          .join(' + ')
        return `${fixings}${FIELD_SEPARATOR}${fmtDateShort(first.date)}${FIELD_SEPARATOR}${first.hour}`
      }
    }
  }
  const head = instruments.slice(0, limit).map((contract) => {
    const matchingProduct =
      markets.length === 1 ? markets[0] :
      parseRdnContractCode(contract) ? 'rdn' :
      rdbeeInstrumentLabel(contract) ? 'rdbee' :
      contract.includes('_') && /^(BASE|PEAK|OFFPEAK)_/.test(contract) ? 'rtpe' :
      ''
    return instrumentLabel(matchingProduct, contract)
  }).join(LIST_SEPARATOR)
  return head
}

function sideVerb(side: string): string {
  return side.toLowerCase() === 'sell' ? 'Sell' : 'Buy'
}

function sideTextClass(side: string): string {
  return side.toLowerCase() === 'sell' ? 'text-red-700' : 'text-emerald-700'
}

type DisplayExposureLeg = {
  product: string
  contract_code: string
  side: string
  fillMWh: number
  intentMWh: number
  otherMWh: number
}

function displayExposureLegs(legs: ApiIntervalExposureLeg[] | undefined): DisplayExposureLeg[] {
  if (!legs || legs.length === 0) return []
  const byKey = new Map<string, DisplayExposureLeg>()
  for (const leg of legs) {
    const key = `${leg.product}\x00${leg.contract_code}\x00${leg.side}`
    let display = byKey.get(key)
    if (!display) {
      display = {
        product: leg.product,
        contract_code: leg.contract_code,
        side: leg.side,
        fillMWh: 0,
        intentMWh: 0,
        otherMWh: 0,
      }
      byKey.set(key, display)
    }
    if (leg.source === 'intent') display.intentMWh += leg.mwh
    else if (leg.source === 'fill') display.fillMWh += leg.mwh
    else display.otherMWh += leg.mwh
  }
  return [...byKey.values()].sort((a, b) =>
    a.product.localeCompare(b.product) ||
    a.contract_code.localeCompare(b.contract_code) ||
    a.side.localeCompare(b.side),
  )
}

function exposureDisplayLegSourceLabel(leg: DisplayExposureLeg): string | null {
  const hasFill = leg.fillMWh > 1e-9
  const hasIntent = leg.intentMWh > 1e-9
  const hasOther = leg.otherMWh > 1e-9
  if (hasFill && hasIntent) return 'filled + intent'
  if (hasIntent) return 'intent'
  if (hasFill && hasOther) return 'filled + other'
  if (hasOther) return 'other'
  return null
}

function exposureDisplayLegText(leg: DisplayExposureLeg, includeSource = true): string {
  const source = exposureDisplayLegSourceLabel(leg)
  const instrument = instrumentLabel(leg.product, leg.contract_code)
  if (includeSource && source === 'intent') return `${sideVerb(leg.side)} intent ${instrument}`
  if (includeSource && source) return `${sideVerb(leg.side)} ${instrument} (${source})`
  return `${sideVerb(leg.side)} ${instrument}`
}

function exposureDisplayLegTitle(leg: DisplayExposureLeg): string {
  const parts: string[] = []
  if (leg.fillMWh > 1e-9) parts.push(`filled ${fmtPrice(leg.fillMWh)} MWh`)
  if (leg.intentMWh > 1e-9) parts.push(`intent ${fmtPrice(leg.intentMWh)} MWh`)
  if (leg.otherMWh > 1e-9) parts.push(`other ${fmtPrice(leg.otherMWh)} MWh`)
  return `${exposureDisplayLegText(leg, false)}${parts.length > 0 ? ` (${parts.join(', ')})` : ''}`
}

function exposureLegTitle(legs: ApiIntervalExposureLeg[], markets: string[], instruments: string[]): string {
  const displayLegs = displayExposureLegs(legs)
  if (displayLegs.length > 0) {
    return displayLegs.map(exposureDisplayLegTitle).join(LIST_SEPARATOR)
  }
  return instruments.length > 0 ? instruments.join(LIST_SEPARATOR) : markets.map(productLabel).join(' × ')
}

function mergeIntervalLegs(target: ApiIntervalExposureLeg[], next: ApiIntervalExposureLeg[] | undefined): ApiIntervalExposureLeg[] {
  if (!next || next.length === 0) return target
  const byKey = new Map<string, ApiIntervalExposureLeg>()
  for (const leg of target) {
    byKey.set(`${leg.product}\x00${leg.contract_code}\x00${leg.side}\x00${leg.source}`, { ...leg })
  }
  for (const leg of next) {
    const key = `${leg.product}\x00${leg.contract_code}\x00${leg.side}\x00${leg.source}`
    const current = byKey.get(key)
    if (current) current.mwh += leg.mwh
    else byKey.set(key, { ...leg })
  }
  return [...byKey.values()].sort((a, b) =>
    a.source.localeCompare(b.source) ||
    a.product.localeCompare(b.product) ||
    a.contract_code.localeCompare(b.contract_code) ||
    a.side.localeCompare(b.side),
  )
}

function ExposureLegList({
  legs,
  markets,
  instruments,
  limit = 4,
}: {
  legs?: ApiIntervalExposureLeg[]
  markets: string[]
  instruments: string[]
  limit?: number
}) {
  const displayLegs = displayExposureLegs(legs)
  const visibleLegs = displayLegs.slice(0, limit)
  if (visibleLegs.length === 0) {
    return <span className="whitespace-normal break-words leading-5">{exposureInstrumentLabel(markets, instruments, 3)}</span>
  }
  if (displayLegs.length > limit) {
    return <span className="whitespace-normal break-words leading-5">{displayLegs.length} various instruments</span>
  }
  return (
    <span className="whitespace-normal break-words leading-5">
      {visibleLegs.map((leg, idx) => (
        <Fragment key={`${leg.product}:${leg.contract_code}:${leg.side}:${idx}`}>
          {idx > 0 && <span className="text-gray-400"> {LIST_SEPARATOR} </span>}
          <span className={`font-semibold ${sideTextClass(leg.side)}`}>{sideVerb(leg.side)}</span>
          {exposureDisplayLegSourceLabel(leg) === 'intent' && <span className="text-amber-700"> intent</span>}
          <span>{` ${instrumentLabel(leg.product, leg.contract_code)}`}</span>
          {(() => {
            const source = exposureDisplayLegSourceLabel(leg)
            return source && source !== 'intent'
              ? <span className="text-gray-500"> ({source})</span>
              : null
          })()}
        </Fragment>
      ))}
    </span>
  )
}

function marketClockFromPaper(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Date(d.getTime() - PAPER_MARKET_DELAY_MS).toISOString()
}

function deliveryLabel(product: string, contract: string): string | null {
  if (product === 'rdn') {
    const parsed = parseRdnContractCode(contract)
    if (!parsed) return null
    const endHour = Number(parsed.hour.slice(1))
    if (!Number.isFinite(endHour) || endHour < 1 || endHour > 24) return null
    const start = String(endHour - 1).padStart(2, '0')
    return `delivery ${parsed.date} ${start}:00-${start}:59`
  }
  if (product === 'rdbee') {
    const parsed = parseRdbeeContractCode(contract)
    if (!parsed) return null
    const startDate = parsed.startIso.slice(0, 10)
    const endDate = parsed.endIso.slice(0, 10)
    return startDate === endDate
      ? `delivery ${startDate} ${fmtTimeOnly(parsed.startIso)}-${fmtTimeOnly(parsed.endIso)}`
      : `delivery ${startDate} ${fmtTimeOnly(parsed.startIso)} → ${endDate} ${fmtTimeOnly(parsed.endIso)}`
  }
  if (product === 'rtpe') {
    const [profile, period] = contract.split('_', 2)
    if (!profile || !period) return 'delivery basket'
    return `delivery basket ${profile} ${period}`
  }
  return null
}

function orderPriceLabel(order: {
  product?: string
  contract_code?: string
}): string {
  if (order.product === 'rdbee') return 'realized 15m VWAP'
  if (order.contract_code?.endsWith('_F1')) return 'realized F1'
  if (order.contract_code?.endsWith('_F2')) return 'realized F2'
  return 'realized price'
}

function paperTradeStatus(group: ApiDailyPnLGroup): string {
  const priced = group.legs.filter((leg) => leg.avg_fill_px != null && leg.filled_mwh > 0).length
  if (priced === 0) return 'intent submitted'
  if (priced < group.legs.length) return 'partially priced'
  if (Math.abs(group.buy_mwh - group.sell_mwh) < 0.000001) return 'priced'
  return 'unbalanced'
}

const PAPER_STATUS_BADGE: Record<string, string> = {
  'priced':           'bg-green-50 text-green-700 border-green-200',
  'partially priced': 'bg-amber-50 text-amber-700 border-amber-200',
  'intent submitted': 'bg-slate-50 text-slate-700 border-slate-200',
  'unbalanced':    'bg-red-50 text-red-700 border-red-200',
}

// Worst-state rollup using the same vocabulary as pair statuses.
function rollupStatus(groups: ApiDailyPnLGroup[]): string {
  const statuses = groups.map(paperTradeStatus)
  if (statuses.some((s) => s === 'unbalanced')) return 'unbalanced'
  if (statuses.some((s) => s === 'partially priced')) return 'partially priced'
  if (statuses.some((s) => s === 'intent submitted')) return 'intent submitted'
  return 'priced'
}

function PaperStatusBadge({ status }: { status: string }) {
  const c = PAPER_STATUS_BADGE[status] ?? 'bg-gray-50 text-gray-600 border-gray-200'
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] rounded-full border font-medium uppercase whitespace-nowrap ${c}`}>
      {status}
    </span>
  )
}

function ExposureStatusBadge({ status }: { status: string }) {
  const normalized = status.toUpperCase()
  const c =
    normalized === 'FILLED FLAT' ? 'bg-green-50 text-green-700 border-green-200' :
    normalized === 'PENDING FLAT' ? 'bg-amber-50 text-amber-700 border-amber-200' :
    normalized.startsWith('OPEN ') ? 'bg-amber-50 text-amber-700 border-amber-200' :
    normalized.startsWith('PARTIAL ') ? 'bg-sky-50 text-sky-700 border-sky-200' :
    normalized.startsWith('UNCLOSEABLE ') ? 'bg-red-50 text-red-700 border-red-200' :
    normalized === 'MIXED' ? 'bg-slate-50 text-slate-700 border-slate-200' :
    'bg-gray-50 text-gray-600 border-gray-200'
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] rounded-full border font-medium uppercase whitespace-nowrap ${c}`}>
      {normalized}
    </span>
  )
}

function isUncloseableStatus(status: string): boolean {
  return status.toUpperCase().startsWith('UNCLOSEABLE ')
}

function isOpenExposureStatus(status: string): boolean {
  const normalized = status.toUpperCase()
  return normalized.startsWith('OPEN ') || normalized.startsWith('PARTIAL ') || normalized.startsWith('UNCLOSEABLE ')
}

function exposureStatusPriority(status: string): number {
  const normalized = status.toUpperCase()
  if (normalized.startsWith('UNCLOSEABLE ')) return 0
  if (normalized.startsWith('PARTIAL ')) return 1
  if (normalized.startsWith('OPEN ')) return 2
  if (normalized === 'PENDING FLAT') return 3
  if (normalized === 'FILLED FLAT') return 4
  return 5
}

function aggregateExposureStatus(rows: ApiIntervalExposureRow[]): string {
  if (rows.some((row) => isUncloseableStatus(row.status))) {
    return rows.some((row) => row.status.toUpperCase().endsWith('SHORT')) ? 'UNCLOSEABLE SHORT' : 'UNCLOSEABLE LONG'
  }
  if (rows.some((row) => row.status.toUpperCase().startsWith('PARTIAL '))) {
    return rows.some((row) => row.status.toUpperCase() === 'PARTIAL SHORT') ? 'PARTIAL SHORT' : 'PARTIAL LONG'
  }
  if (rows.some((row) => row.status.toUpperCase().startsWith('OPEN '))) {
    return rows.some((row) => row.status.toUpperCase() === 'OPEN SHORT') ? 'OPEN SHORT' : 'OPEN LONG'
  }
  if (rows.some((row) => row.status.toUpperCase() === 'PENDING FLAT')) return 'PENDING FLAT'
  return 'FILLED FLAT'
}


function commonPaperContract(group: ApiDailyPnLGroup): string {
  const first = group.legs[0]?.contract_code ?? group.group_id
  const parsed = parseRdnContractCode(first)
  if (parsed) return `${fmtDateShort(parsed.date)}${FIELD_SEPARATOR}${parsed.hour}`
  return first.replace(/_F[12]$/, '')
}

function paperLeg(group: ApiDailyPnLGroup, suffix: '_F1' | '_F2'): ApiDailyPnLLeg | undefined {
  return group.legs.find((leg) => leg.contract_code.endsWith(suffix))
}

function legLabel(leg: ApiDailyPnLLeg | undefined): string {
  if (!leg) return '—'
  if (leg.avg_fill_px == null || leg.filled_mwh <= 0) return `${leg.side} awaiting price`
  return `${leg.side} @ ${fmtPrice(leg.avg_fill_px)}`
}

function groupSizeMWh(group: ApiDailyPnLGroup): number {
  const completed = Math.min(group.buy_mwh, group.sell_mwh)
  if (completed > 0) return completed
  return Math.max(...group.legs.map((leg) => leg.filled_mwh), 0)
}

function paperDirection(group: ApiDailyPnLGroup): string {
  const f1 = paperLeg(group, '_F1')
  const f2 = paperLeg(group, '_F2')
  if (f1 && f2) {
    if (f1.side === 'buy' && f2.side === 'sell') return 'LONG'
    if (f1.side === 'sell' && f2.side === 'buy') return 'SHORT'
  }
  if (f1?.side === 'buy') return 'PARTIAL LONG'
  if (f1?.side === 'sell') return 'PARTIAL SHORT'
  if (f2?.side === 'sell') return 'PARTIAL LONG'
  if (f2?.side === 'buy') return 'PARTIAL SHORT'
  return group.legs[0] ? 'PARTIAL' : '—'
}

function paperDelta(group: ApiDailyPnLGroup): number | null {
  const f1 = paperLeg(group, '_F1')
  const f2 = paperLeg(group, '_F2')
  if (!f1 || !f2 || f1.avg_fill_px == null || f2.avg_fill_px == null) return null
  if (f1.side === 'buy' && f2.side === 'sell') return f2.avg_fill_px - f1.avg_fill_px
  if (f1.side === 'sell' && f2.side === 'buy') return f1.avg_fill_px - f2.avg_fill_px
  const size = groupSizeMWh(group)
  return size > 0 ? group.pnl_pln / size : null
}

function DailyTradeBreakdown({ groups }: { groups: ApiDailyPnLGroup[] }) {
  if (groups.length === 0) {
    return <div className="px-3 py-3 text-xs text-gray-500">No realized trade pairs for this day.</div>
  }
  const sorted = [...groups].sort(
    (a, b) =>
      commonPaperContract(b).localeCompare(commonPaperContract(a)) ||
      b.group_id.localeCompare(a.group_id),
  )
  return (
    <table className="w-full text-xs bg-gray-50">
      <thead className="border-b border-gray-200 text-left text-[10px] uppercase text-gray-500">
        <tr>
          <th className="px-3 py-2 pl-9">Arbitrage pair</th>
          <th className="px-3 py-2">Direction</th>
          <th className="px-3 py-2">F1 leg</th>
          <th className="px-3 py-2">F2 leg</th>
          <th className="px-3 py-2 text-right">Size MWh</th>
          <th className="px-3 py-2 text-right">Delta</th>
          <th className="px-3 py-2 text-right">P&L</th>
          <th className="px-3 py-2">Status</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((group) => {
          const delta = paperDelta(group)
          const balanced = Math.abs(group.buy_mwh - group.sell_mwh) < 0.000001 && group.buy_mwh > 0
          const dateMeta = tradeDateMeta(group)
          const status = paperTradeStatus(group)
          return (
            <tr key={group.group_id} className={`border-b border-gray-100 ${status === 'unbalanced' ? 'bg-red-50/50' : ''}`}>
              <td className="px-3 py-2 pl-9">
                <div className="font-mono text-gray-700">{commonPaperContract(group)}</div>
                {dateMeta && (
                  <div className="mt-0.5 text-[10px] text-gray-400">{dateMeta}</div>
                )}
              </td>
              <td className="px-3 py-2 font-semibold text-gray-700">{paperDirection(group)}</td>
              <td className="px-3 py-2 font-mono text-gray-600">{group.product === 'rdn' ? legLabel(paperLeg(group, '_F1')) : 'N/A'}</td>
              <td className="px-3 py-2 font-mono text-gray-600">{group.product === 'rdn' ? legLabel(paperLeg(group, '_F2')) : 'N/A'}</td>
              <td className="px-3 py-2 text-right font-mono">{fmtPrice(groupSizeMWh(group))}</td>
              <td className={`px-3 py-2 text-right font-mono ${delta == null ? 'text-gray-500' : pnlText(delta)}`}>
                {delta == null ? '—' : formatSignedMoney(delta)}
              </td>
              <td className={`px-3 py-2 text-right font-mono font-semibold ${balanced ? pnlText(group.pnl_pln) : 'text-gray-500'}`}>
                {balanced ? formatSignedMoney(group.pnl_pln) : '—'}
              </td>
              <td className="px-3 py-2"><PaperStatusBadge status={status} /></td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function StatusPill({ status }: { status: string }) {
  const c =
    status === 'filled' ? 'bg-green-50 text-green-700 border-green-200' :
    status === 'open' ? 'bg-slate-50 text-slate-700 border-slate-200' :
    status === 'partial' ? 'bg-amber-50 text-amber-700 border-amber-200' :
    status === 'unfilled' ? 'bg-amber-50 text-amber-700 border-amber-200' :
    status === 'cancelled' || status === 'expired' ? 'bg-gray-50 text-gray-600 border-gray-200' :
    'bg-red-50 text-red-700 border-red-200'
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] rounded-full border font-medium uppercase ${c}`}>
      {status}
    </span>
  )
}

function SideBadge({ side }: { side: string }) {
  const c = side === 'buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded font-semibold uppercase ${c}`}>
      {side}
    </span>
  )
}


type SummaryCard = {
  label: string
  value: string
  colorCls: string
  unit?: string
  highlight?: boolean
}

function SummaryCards({ cards }: { cards: SummaryCard[] }) {
  return (
    <div className="flex gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
      {cards.map((card) => (
        <Card key={card.label} {...card} />
      ))}
    </div>
  )
}


function DailyPnLCards({ rows }: { rows: ApiDailyPnLRow[] }) {
  const closedPnl = rows.reduce((sum, row) => sum + row.realized_pnl_pln, 0)
  const closedGroups = rows.reduce((sum, row) => sum + row.completed_groups, 0)
  const openGroups = rows.reduce((sum, row) => sum + row.open_groups, 0)
  return <SummaryCards cards={[
    { label: 'Closed P&L', value: closedPnl.toFixed(2), colorCls: pnlText(closedPnl), unit: 'PLN', highlight: true },
    { label: 'Closed groups', value: String(closedGroups), colorCls: 'text-gray-900' },
    { label: 'Open groups', value: String(openGroups), colorCls: openGroups > 0 ? 'text-amber-700' : 'text-gray-900' },
  ]} />
}

function Card({
  label, value, colorCls, unit, highlight,
}: SummaryCard) {
  return (
    <div className={`flex-1 px-4 py-2 rounded-md border ${highlight ? 'bg-white border-gray-300' : 'bg-white border-gray-200'}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className={`text-lg font-semibold ${colorCls}`}>
        {value}{unit && <span className="text-xs font-normal text-gray-400"> {unit}</span>}
      </div>
    </div>
  )
}

type IntervalGroup = {
  key: string
  markets: string[]
  instruments: string[]
  legs: ApiIntervalExposureLeg[]
  status: string
  firstInterval: string
  lastInterval: string
  closeDeadline: string
  lastCloseDeadline: string
  intervalCount: number
  netMWh: number
  intentMWh: number
  cashFlow: number
  realizedPnL: number
  unrealizedPnL: number | null
  rows: ApiIntervalExposureRow[]
}

function intervalExposure(group: ApiIntervalExposureRow): number {
  return group.net_mwh
}

function buildIntervalGroups(intervals: ApiIntervalExposureRow[]): IntervalGroup[] {
  const byKey = new Map<string, IntervalGroup>()
  for (const row of intervals) {
    const markets = [...row.markets].sort()
    const instruments = [...row.instruments].sort()
    const key = `${markets.join('|')}::${instruments.join('|')}::${row.status}`
    let group = byKey.get(key)
    if (!group) {
      group = {
        key,
        markets,
        instruments,
        legs: [],
        status: row.status,
        firstInterval: row.delivery_interval,
        lastInterval: row.delivery_interval,
        closeDeadline: row.close_deadline,
        lastCloseDeadline: row.close_deadline,
        intervalCount: 0,
        netMWh: 0,
        intentMWh: 0,
        cashFlow: 0,
        realizedPnL: 0,
        unrealizedPnL: null,
        rows: [],
      }
      byKey.set(key, group)
    }
    group.rows.push(row)
    group.legs = mergeIntervalLegs(group.legs, row.legs)
    group.intervalCount += 1
    group.netMWh += row.net_mwh
    group.intentMWh += row.intent_mwh
    group.cashFlow += row.cash_flow_pln
    group.realizedPnL += row.realized_pnl_pln
    if (row.unrealized_pnl_pln != null) {
      group.unrealizedPnL = (group.unrealizedPnL ?? 0) + row.unrealized_pnl_pln
    }
    if (row.delivery_interval < group.firstInterval) group.firstInterval = row.delivery_interval
    if (row.delivery_interval > group.lastInterval) group.lastInterval = row.delivery_interval
    if (row.close_deadline < group.closeDeadline) group.closeDeadline = row.close_deadline
    if (row.close_deadline > group.lastCloseDeadline) group.lastCloseDeadline = row.close_deadline
  }
  return [...byKey.values()]
    .map((group) => ({
      ...group,
      rows: [...group.rows].sort((a, b) => a.delivery_interval.localeCompare(b.delivery_interval)),
    }))
    .sort(
      (a, b) =>
        exposureStatusPriority(a.status) - exposureStatusPriority(b.status) ||
        a.firstInterval.localeCompare(b.firstInterval) ||
        a.instruments.join('|').localeCompare(b.instruments.join('|')),
    )
}

function PositionsTab({ filters }: { filters: TradesSearch }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const apiFilters = {
    thesis_id: filters.thesis,
    product: filters.product,
    status: filters.status,
    contract: filters.contract,
  }
  const { data, isLoading, error } = useExposureIntervals(apiFilters)
  const intervals = useMemo(() => data?.intervals ?? [], [data?.intervals])
  const groups = useMemo(() => buildIntervalGroups(intervals), [intervals])
  const openMWh = intervals.reduce((s, row) => s + Math.abs(intervalExposure(row)), 0)
  const unrealizedValues = intervals
    .map((row) => row.unrealized_pnl_pln)
    .filter((value): value is number => value != null)
  const unrealized = unrealizedValues.reduce((s, value) => s + value, 0)
  const uncloseable = intervals.filter((row) => isUncloseableStatus(row.status)).length

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  if (error) return <Empty message="Couldn't load positions." />
  if (isLoading) return <Empty message="Loading…" />
  if (groups.length === 0) return <Empty message="No delivery-interval positions match your filters." />

  return (
    <>
      <SummaryCards cards={[
        { label: 'Open exposure', value: openMWh.toFixed(2), colorCls: openMWh > 0 ? 'text-amber-700' : 'text-gray-900', unit: 'MWh', highlight: true },
        { label: 'Cash flow', value: (data?.total_cash_flow_pln ?? 0).toFixed(2), colorCls: pnlText(data?.total_cash_flow_pln ?? 0), unit: 'PLN' },
        { label: 'Realized P&L', value: (data?.total_realized_pln ?? 0).toFixed(2), colorCls: pnlText(data?.total_realized_pln ?? 0), unit: 'PLN' },
        { label: 'Unrealized P&L', value: unrealizedValues.length > 0 ? unrealized.toFixed(2) : '—', colorCls: optionalPnlText(unrealizedValues.length > 0 ? unrealized : null), unit: unrealizedValues.length > 0 ? 'PLN' : undefined },
        { label: 'Uncloseable 15min buckets', value: String(uncloseable), colorCls: uncloseable > 0 ? 'text-red-700' : 'text-gray-900' },
      ]} />
      <div className="flex-1 overflow-auto">
        <table className="w-full min-w-[1400px] text-xs table-fixed">
          <colgroup>
            <col className="w-64" />
            <col className="w-56" />
            <col className="w-24" />
            <col className="w-20" />
            <col className="w-24" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-52" />
          </colgroup>
          <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
            <tr className="text-left text-gray-500 uppercase text-[10px]">
              <th className="px-3 py-2">Lifecycle / instruments</th>
              <th className="px-3 py-2">Delivery window</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Net MWh</th>
              <th className="px-3 py-2 text-right">Intent MWh</th>
              <th className="px-3 py-2 text-right">Cash flow</th>
              <th className="px-3 py-2 text-right">Realized</th>
              <th className="px-3 py-2 text-right">Unrealized</th>
              <th className="px-3 py-2">Close deadline</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const isExpanded = expanded.has(group.key)
              const exposure = group.netMWh
              return (
                <Fragment key={group.key}>
                  <tr
                    onClick={() => toggle(group.key)}
                    className={`cursor-pointer border-b border-gray-100 ${isUncloseableStatus(group.status) ? 'bg-red-50/50 hover:bg-red-50' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        <span className="font-semibold text-gray-800">{group.markets.map(productLabel).join(' × ')}</span>
                        <span className="text-gray-400">({group.intervalCount} buckets of 15min)</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-600 whitespace-normal break-words leading-5" title={exposureLegTitle(group.legs, group.markets, group.instruments)}>
                        <ExposureLegList legs={group.legs} markets={group.markets} instruments={group.instruments} />
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top text-gray-700 whitespace-nowrap">
                      {fmtDeliveryWindow(group.firstInterval, group.lastInterval)}
                    </td>
                    <td className="px-3 py-2 align-top"><ExposureStatusBadge status={group.status} /></td>
                    <td className={`px-3 py-2 text-right font-semibold ${exposure > 0 ? 'text-green-700' : exposure < 0 ? 'text-red-700' : 'text-gray-600'}`}>
                      {formatSignedMWh(exposure)}
                    </td>
                    <td className={`px-3 py-2 text-right ${group.intentMWh !== 0 ? 'text-amber-700' : 'text-gray-500'}`}>
                      {formatSignedMWh(group.intentMWh)}
                    </td>
                    <td className={`px-3 py-2 text-right ${pnlText(group.cashFlow)}`}>{formatSignedMoney(group.cashFlow)}</td>
                    <td className={`px-3 py-2 text-right ${pnlText(group.realizedPnL)}`}>{formatSignedMoney(group.realizedPnL)}</td>
                    <td className={`px-3 py-2 text-right ${optionalPnlText(group.unrealizedPnL)}`}>
                      {formatOptionalMoney(group.unrealizedPnL)}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDateTimeRange(group.closeDeadline, group.lastCloseDeadline)}</td>
                  </tr>
                  {isExpanded && group.rows.map((row) => {
                    const rowExposure = intervalExposure(row)
                    return (
                      <tr key={`${group.key}:${row.delivery_interval}`} className="border-b border-gray-100 bg-gray-50/60">
                        <td className="px-3 py-2 pl-8 align-top text-[11px] text-gray-700 whitespace-normal break-words leading-5" title={exposureLegTitle(row.legs ?? [], row.markets, row.instruments)}>
                          <ExposureLegList legs={row.legs} markets={row.markets} instruments={row.instruments} />
                        </td>
                        <td className="px-3 py-2 align-top text-gray-600 whitespace-nowrap">{fmtDeliveryWindow(row.delivery_interval, row.delivery_interval)}</td>
                        <td className="px-3 py-2 align-top"><ExposureStatusBadge status={row.status} /></td>
                        <td className={`px-3 py-2 text-right ${rowExposure > 0 ? 'text-green-700' : rowExposure < 0 ? 'text-red-700' : 'text-gray-600'}`}>
                          {formatSignedMWh(rowExposure)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">{formatSignedMWh(row.intent_mwh)}</td>
                        <td className={`px-3 py-2 text-right ${pnlText(row.cash_flow_pln ?? 0)}`}>{formatSignedMoney(row.cash_flow_pln ?? 0)}</td>
                        <td className={`px-3 py-2 text-right ${pnlText(row.realized_pnl_pln)}`}>{formatSignedMoney(row.realized_pnl_pln)}</td>
                        <td className={`px-3 py-2 text-right ${optionalPnlText(row.unrealized_pnl_pln ?? null)}`}>
                          {formatOptionalMoney(row.unrealized_pnl_pln ?? null)}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{fmtDateTimeShort(row.close_deadline)}</td>
                      </tr>
                    )
                  })}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

function ExecutionsTab({ filters }: { filters: TradesSearch }) {
  const resetKey = [
    filters.thesis?.join(',') ?? '',
    filters.product?.join(',') ?? '',
    filters.status?.join(',') ?? '',
    filters.contract ?? '',
    filters.from ?? '',
    filters.to ?? '',
  ].join('|')
  const [page, setPage] = useState({ key: resetKey, offset: 0 })
  const offset = page.key === resetKey ? page.offset : 0
  const setOffset = (nextOffset: number) => setPage({ key: resetKey, offset: nextOffset })
  const apiFilters = {
    thesis_id: filters.thesis,
    product: filters.product,
    status: filters.status,
    contract: filters.contract,
    from: filters.from,
    to: filters.to,
    limit: TABLE_PAGE_SIZE,
    offset,
  }
  const fillFilters = {
    thesis_id: filters.thesis,
    product: filters.product,
    contract: filters.contract,
    from: filters.from,
    to: filters.to,
    limit: TABLE_PAGE_SIZE,
    offset,
  }
  const { data: tradesData, isLoading: ordersLoading, error: ordersError } = useTradesList(apiFilters)
  const { data: fillsData, isLoading: fillsLoading, error: fillsError } = useFillsList(fillFilters)
  const hasStatusFilter = (filters.status?.length ?? 0) > 0
  const orders = useMemo(() => tradesData?.trades ?? [], [tradesData?.trades])
  const fills = useMemo(
    () => hasStatusFilter ? [] : (fillsData?.fills ?? []),
    [fillsData?.fills, hasStatusFilter],
  )
  const ordersById = useMemo(() => {
    const byId = new Map<string, ApiTradeRow>()
    for (const order of orders) byId.set(order.id, order)
    return byId
  }, [orders])

  const events = useMemo(() => {
    type EventRow =
      | { kind: 'intent'; time: string; paperTime?: string | null; order: ApiTradeRow; fill?: never }
      | { kind: 'fill'; time: string; paperTime?: string | null; fill: ApiFillRow; order?: never }
    const rows: EventRow[] = [
      ...orders.map((order) => ({
        kind: 'intent' as const,
        time: order.simulated_submitted_at ?? order.submitted_at,
        paperTime: order.submitted_at,
        order,
      })),
      ...fills.map((fill) => ({
        kind: 'fill' as const,
        time: fill.market_filled_at ?? marketClockFromPaper(fill.filled_at),
        paperTime: ordersById.get(fill.order_id)?.submitted_at ?? null,
        fill,
      })),
    ]
    return rows.sort(
      (a, b) =>
        b.time.localeCompare(a.time) ||
        (b.kind === 'intent' ? b.order.contract_code : b.fill.contract_code).localeCompare(
          a.kind === 'intent' ? a.order.contract_code : a.fill.contract_code,
        ),
    )
  }, [orders, fills, ordersById])

  const filledMWh = fills.reduce((s, fill) => s + fill.fill_size_mwh, 0)
  const cash = fills.reduce((s, fill) => s + (fill.side === 'sell' ? 1 : -1) * fill.fill_size_mwh * fill.fill_price_pln, 0)
  const pageCount = Math.max(tradesData?.count ?? 0, hasStatusFilter ? 0 : (fillsData?.count ?? 0))

  if (ordersError || fillsError) return <Empty message="Couldn't load executions." />
  if (ordersLoading || fillsLoading) return <Empty message="Loading…" />
  if (events.length === 0) return <Empty message="No executions match your filters." />

  return (
    <>
      <SummaryCards cards={[
        { label: 'Intents', value: String(orders.length), colorCls: 'text-gray-900', highlight: true },
        { label: 'Fills', value: String(fills.length), colorCls: fills.length > 0 ? 'text-green-700' : 'text-gray-900' },
        { label: 'Filled MWh', value: filledMWh.toFixed(2), colorCls: 'text-gray-900', unit: 'MWh' },
        { label: 'Fill cash', value: cash.toFixed(2), colorCls: pnlText(cash), unit: 'PLN' },
      ]} />
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
            <tr className="text-left text-gray-500 uppercase text-[10px]">
              <th className="px-3 py-2">Market time / paper time</th>
              <th className="px-3 py-2">Thesis</th>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Instrument / delivery</th>
              <th className="px-3 py-2">Side</th>
              <th className="px-3 py-2 text-right">MWh</th>
              <th className="px-3 py-2 text-right">Price</th>
              <th className="px-3 py-2">Order status / fill source</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => {
              const row = event.kind === 'intent' ? event.order : event.fill
              const delivery = deliveryLabel(row.product, row.contract_code)
              return (
                <tr key={`${event.kind}:${row.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="text-gray-800">{fmtTime(event.time)}</div>
                    <div className="text-[10px] text-gray-400">
                      {event.paperTime ? fmtTime(event.paperTime) : '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {row.thesis_id ? (
                      <Link
                        to="/theses/$thesisId"
                        params={{ thesisId: row.thesis_id }}
                        className="text-slate-700 hover:underline"
                      >
                        {row.thesis_name || row.thesis_id.slice(0, 8)}
                      </Link>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 uppercase font-semibold text-gray-700">{event.kind}</td>
                  <td className="px-3 py-2 font-semibold text-gray-700">{productLabel(row.product)}</td>
                  <td className="px-3 py-2">
                    <div className="text-[11px] text-gray-700" title={row.contract_code}>
                      {instrumentLabel(row.product, row.contract_code)}
                    </div>
                    {delivery && <div className="mt-0.5 text-[10px] text-gray-400">{delivery}</div>}
                  </td>
                  <td className="px-3 py-2"><SideBadge side={row.side} /></td>
                  <td className="px-3 py-2 text-right">
                    {event.kind === 'intent' ? fmtPrice(event.order.size_mwh) : fmtPrice(event.fill.fill_size_mwh)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {event.kind === 'intent'
                      ? (event.order.avg_fill_px != null ? fmtPrice(event.order.avg_fill_px) : '—')
                      : fmtPrice(event.fill.fill_price_pln)}
                  </td>
                  <td className="px-3 py-2">
                    {event.kind === 'intent' ? (
                      <StatusPill status={event.order.status} />
                    ) : (
                      <span className="font-mono text-[11px] text-gray-500">{event.fill.source_trade_id ?? event.fill.order_id.slice(0, 8)}</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <PageControls count={pageCount} limit={TABLE_PAGE_SIZE} offset={offset} onOffsetChange={setOffset} noun="source rows" />
    </>
  )
}

function summarizeIntervalRows(rows: ApiIntervalExposureRow[]) {
  const cashFlow = rows.reduce((s, row) => s + (row.cash_flow_pln ?? 0), 0)
  const realized = rows.reduce((s, row) => s + row.realized_pnl_pln, 0)
  const net = rows.reduce((s, row) => s + intervalExposure(row), 0)
  const unrealizedValues = rows
    .map((row) => row.unrealized_pnl_pln)
    .filter((value): value is number => value != null)
  const unrealized = unrealizedValues.length > 0 ? unrealizedValues.reduce((s, v) => s + v, 0) : null
  const markets = [...new Set(rows.flatMap((row) => row.markets))].sort()
  const instruments = [...new Set(rows.flatMap((row) => row.instruments))].sort()
  const legs = rows.reduce<ApiIntervalExposureLeg[]>((merged, row) => mergeIntervalLegs(merged, row.legs), [])
  const status = aggregateExposureStatus(rows)
  const bucketCount = new Set(rows.map((row) => row.delivery_interval)).size
  return { cashFlow, realized, net, unrealized, markets, instruments, legs, status, bucketCount }
}

function thesisDisplayLabel(row: ApiIntervalExposureRow): string {
  return row.thesis_name || (row.thesis_id ? row.thesis_id.slice(0, 8) : 'Manual / unattributed')
}

function groupIntervalsByThesis(rows: ApiIntervalExposureRow[]): [string, ApiIntervalExposureRow[]][] {
  const byThesis = new Map<string, ApiIntervalExposureRow[]>()
  for (const row of rows) {
    const key = row.thesis_id ?? ''
    const list = byThesis.get(key)
    if (list) list.push(row)
    else byThesis.set(key, [row])
  }
  return [...byThesis.entries()].sort(
    (a, b) =>
      b[1].reduce((s, r) => s + r.realized_pnl_pln, 0) - a[1].reduce((s, r) => s + r.realized_pnl_pln, 0),
  )
}

function PnLTab({ filters }: { filters: TradesSearch }) {
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set())
  const [expandedTheses, setExpandedTheses] = useState<Set<string>>(new Set())
  const apiFilters = {
    thesis_id: filters.thesis,
    product: filters.product,
    status: filters.status,
    contract: filters.contract,
    group_by: 'thesis' as const,
  }
  const { data, isLoading, error } = useExposureIntervals(apiFilters)
  const intervals = useMemo(() => data?.intervals ?? [], [data?.intervals])
  const byDay = useMemo(() => {
    const days = new Map<string, ApiIntervalExposureRow[]>()
    for (const row of intervals) {
      const day = row.delivery_interval.slice(0, 10)
      const list = days.get(day)
      if (list) list.push(row)
      else days.set(day, [row])
    }
    return [...days.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [intervals])
  const unrealizedValues = intervals
    .map((row) => row.unrealized_pnl_pln)
    .filter((value): value is number => value != null)
  const totalUnrealized = unrealizedValues.reduce((s, value) => s + value, 0)
  const strategyCount = new Set(intervals.map((row) => row.thesis_id ?? '')).size
  const openBucketCount = new Set(
    intervals.filter((row) => isOpenExposureStatus(row.status)).map((row) => row.delivery_interval),
  ).size

  const toggleDay = (day: string) =>
    setExpandedDays((prev) => {
      const next = new Set(prev)
      if (next.has(day)) next.delete(day)
      else next.add(day)
      return next
    })
  const toggleThesis = (key: string) =>
    setExpandedTheses((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  if (error) return <Empty message="Couldn't load strategy P&L." />
  if (isLoading) return <Empty message="Loading…" />
  if (intervals.length === 0) return <Empty message="No strategy P&L matches your filters." />

  return (
    <>
      <SummaryCards cards={[
        { label: 'Cash flow', value: (data?.total_cash_flow_pln ?? 0).toFixed(2), colorCls: pnlText(data?.total_cash_flow_pln ?? 0), unit: 'PLN' },
        { label: 'Realized P&L', value: (data?.total_realized_pln ?? 0).toFixed(2), colorCls: pnlText(data?.total_realized_pln ?? 0), unit: 'PLN', highlight: true },
        { label: 'Unrealized P&L', value: unrealizedValues.length > 0 ? totalUnrealized.toFixed(2) : '—', colorCls: optionalPnlText(unrealizedValues.length > 0 ? totalUnrealized : null), unit: unrealizedValues.length > 0 ? 'PLN' : undefined },
        { label: 'Strategies', value: String(strategyCount), colorCls: 'text-gray-900' },
        { label: 'Open 15min buckets', value: String(openBucketCount), colorCls: openBucketCount > 0 ? 'text-amber-700' : 'text-gray-900' },
      ]} />
      <div className="flex-1 overflow-auto">
        <table className="w-full min-w-[1816px] text-xs table-fixed">
          <colgroup>
            <col className="w-[768px]" />
            <col className="w-32" />
            <col className="w-[360px]" />
            <col className="w-24" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-28" />
            <col className="w-32" />
          </colgroup>
          <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
            <tr className="text-left text-gray-500 uppercase text-[10px]">
              <th className="px-3 py-2">Delivery day / strategy / bucket</th>
              <th className="px-3 py-2">Products</th>
              <th className="px-3 py-2">Instruments</th>
              <th className="px-3 py-2 text-right w-24">Net MWh</th>
              <th className="px-3 py-2 text-right w-28">Cash flow</th>
              <th className="px-3 py-2 text-right w-28">Realized</th>
              <th className="px-3 py-2 text-right w-28">Unrealized</th>
              <th className="px-3 py-2 w-32">Status</th>
            </tr>
          </thead>
          <tbody>
            {byDay.map(([day, dayRows]) => {
              const dayCollapsed = !expandedDays.has(day)
              const d = summarizeIntervalRows(dayRows)
              const theses = groupIntervalsByThesis(dayRows)
              return (
                <Fragment key={day}>
                  <tr
                    onClick={() => toggleDay(day)}
                    className={`cursor-pointer border-b border-gray-200 ${isUncloseableStatus(d.status) ? 'bg-red-50/60 hover:bg-red-50' : 'bg-gray-50/60 hover:bg-gray-50'}`}
                  >
                    <td className="px-3 py-2 text-gray-700 whitespace-normal break-words">
                      <span className="inline-flex items-center gap-1 font-semibold whitespace-normal break-words">
                        {dayCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                        {fmtDate(day)}
                      </span>
                      <span className="ml-1.5 text-gray-400 whitespace-normal break-words">({theses.length} {theses.length === 1 ? 'strategy' : 'strategies'} · {d.bucketCount} buckets of 15min)</span>
                    </td>
                    <td className="px-3 py-2 font-semibold text-gray-700 whitespace-normal break-words">{d.markets.map(productLabel).join(' × ')}</td>
                    <td className="px-3 py-2 align-top text-[11px] text-gray-600 whitespace-normal break-words leading-5" title={exposureLegTitle(d.legs, d.markets, d.instruments)}>
                      <ExposureLegList legs={d.legs} markets={d.markets} instruments={d.instruments} />
                    </td>
                    <td className={`px-3 py-2 text-right font-semibold ${d.net > 0 ? 'text-green-700' : d.net < 0 ? 'text-red-700' : 'text-gray-600'}`}>
                      {formatSignedMWh(d.net)}
                    </td>
                    <td className={`px-3 py-2 text-right font-semibold ${pnlText(d.cashFlow)}`}>{formatSignedMoney(d.cashFlow)}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${pnlText(d.realized)}`}>{formatSignedMoney(d.realized)}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${optionalPnlText(d.unrealized)}`}>{formatOptionalMoney(d.unrealized)}</td>
                    <td className="px-3 py-2"><ExposureStatusBadge status={d.status} /></td>
                  </tr>
                  {!dayCollapsed && theses.map(([thesisKey, thesisRows]) => {
                    const tkey = `${day}\x00${thesisKey}`
                    const thesisCollapsed = !expandedTheses.has(tkey)
                    const t = summarizeIntervalRows(thesisRows)
                    const thesisId = thesisRows[0].thesis_id
                    const sortedBuckets = [...thesisRows].sort((a, b) => a.delivery_interval.localeCompare(b.delivery_interval))
                    return (
                      <Fragment key={tkey}>
                        <tr
                          onClick={() => toggleThesis(tkey)}
                          className={`cursor-pointer border-b border-gray-100 ${isUncloseableStatus(t.status) ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-3 py-2 pl-8 text-gray-700 whitespace-normal break-words">
                            <span className="inline-flex max-w-full items-start gap-1 whitespace-normal break-words align-top">
                              {thesisCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                              {thesisId ? (
                                <Link
                                  to="/theses/$thesisId"
                                  params={{ thesisId }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="min-w-0 whitespace-normal break-words text-slate-700 hover:underline"
                                >
                                  {thesisDisplayLabel(thesisRows[0])}
                                </Link>
                              ) : (
                                <span className="min-w-0 whitespace-normal break-words text-gray-500">{thesisDisplayLabel(thesisRows[0])}</span>
                              )}
                            </span>
                            <div className="mt-0.5 text-gray-400 whitespace-normal break-words">({t.bucketCount} buckets of 15min)</div>
                          </td>
                          <td className="px-3 py-2 font-semibold text-gray-700 whitespace-normal break-words">{t.markets.map(productLabel).join(' × ')}</td>
                          <td className="px-3 py-2 align-top text-[11px] text-gray-600 whitespace-normal break-words leading-5" title={exposureLegTitle(t.legs, t.markets, t.instruments)}>
                            <ExposureLegList legs={t.legs} markets={t.markets} instruments={t.instruments} />
                          </td>
                          <td className={`px-3 py-2 text-right ${t.net > 0 ? 'text-green-700' : t.net < 0 ? 'text-red-700' : 'text-gray-600'}`}>
                            {formatSignedMWh(t.net)}
                          </td>
                          <td className={`px-3 py-2 text-right ${pnlText(t.cashFlow)}`}>{formatSignedMoney(t.cashFlow)}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${pnlText(t.realized)}`}>{formatSignedMoney(t.realized)}</td>
                          <td className={`px-3 py-2 text-right ${optionalPnlText(t.unrealized)}`}>{formatOptionalMoney(t.unrealized)}</td>
                          <td className="px-3 py-2"><ExposureStatusBadge status={t.status} /></td>
                        </tr>
                        {!thesisCollapsed && sortedBuckets.map((row) => (
                          <tr key={`${tkey}\x00${row.delivery_interval}`} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-2 pl-14 text-gray-600 whitespace-normal">{fmtDeliveryWindow(row.delivery_interval, row.delivery_interval)}</td>
                            <td className="px-3 py-2 font-semibold text-gray-700 whitespace-normal break-words">{row.markets.map(productLabel).join(' × ')}</td>
                            <td className="px-3 py-2 align-top text-[11px] text-gray-600 whitespace-normal break-words leading-5" title={exposureLegTitle(row.legs ?? [], row.markets, row.instruments)}>
                              <ExposureLegList legs={row.legs} markets={row.markets} instruments={row.instruments} />
                            </td>
                            <td className={`px-3 py-2 text-right ${intervalExposure(row) > 0 ? 'text-green-700' : intervalExposure(row) < 0 ? 'text-red-700' : 'text-gray-600'}`}>
                              {formatSignedMWh(intervalExposure(row))}
                            </td>
                            <td className={`px-3 py-2 text-right ${pnlText(row.cash_flow_pln ?? 0)}`}>{formatSignedMoney(row.cash_flow_pln ?? 0)}</td>
                            <td className={`px-3 py-2 text-right ${pnlText(row.realized_pnl_pln)}`}>{formatSignedMoney(row.realized_pnl_pln)}</td>
                            <td className={`px-3 py-2 text-right ${optionalPnlText(row.unrealized_pnl_pln ?? null)}`}>
                              {formatOptionalMoney(row.unrealized_pnl_pln ?? null)}
                            </td>
                            <td className="px-3 py-2"><ExposureStatusBadge status={row.status} /></td>
                          </tr>
                        ))}
                      </Fragment>
                    )
                  })}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

function OldOrdersTab({ filters }: { filters: TradesSearch }) {
  const resetKey = [
    filters.thesis?.join(',') ?? '',
    filters.status?.join(',') ?? '',
    filters.contract ?? '',
    filters.from ?? '',
    filters.to ?? '',
  ].join('|')
  const [page, setPage] = useState({ key: resetKey, offset: 0 })
  const offset = page.key === resetKey ? page.offset : 0
  const setOffset = (nextOffset: number) => setPage({ key: resetKey, offset: nextOffset })
  const apiFilters = {
    thesis_id: filters.thesis,
    product: ['rdn'],
    status: filters.status,
    contract: filters.contract,
    from: filters.from,
    to: filters.to,
    limit: TABLE_PAGE_SIZE,
    offset,
  }
  const { data, isLoading, error } = useTradesList(apiFilters)
  const trades: ApiTradeRow[] = useMemo(
    () =>
      [...(data?.trades ?? [])].sort(
        (a, b) =>
          (b.simulated_submitted_at ?? '').localeCompare(a.simulated_submitted_at ?? '') ||
          b.contract_code.localeCompare(a.contract_code) ||
          a.id.localeCompare(b.id),
      ),
    [data],
  )

  if (error) return <Empty message="Couldn't load intents." />
  if (isLoading) return <Empty message="Loading…" />
  if (trades.length === 0) return <Empty message="No intents match your filters." />

  return (
    <>
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
            <tr className="text-left text-gray-500 uppercase text-[10px]">
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Thesis</th>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Instrument</th>
              <th className="px-3 py-2">Side</th>
              <th className="px-3 py-2 text-right">Settlement</th>
              <th className="px-3 py-2 text-right">Size MWh</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Priced MWh</th>
              <th className="px-3 py-2 text-right">Realized price</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => {
              const dateMeta = tradeDateMeta(t)
              return (
                <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                    <div>{fmtTime(t.submitted_at)}</div>
                    {t.simulated_submitted_at && (
                      <div className="text-[10px] text-gray-400">market {fmtTime(t.simulated_submitted_at)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {t.thesis_id ? (
                      <Link
                        to="/theses/$thesisId"
                        params={{ thesisId: t.thesis_id }}
                        className="text-slate-700 hover:underline"
                      >
                        {t.thesis_name || t.thesis_id.slice(0, 8)}
                      </Link>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 uppercase text-gray-700">{t.product}</td>
                  <td className="px-3 py-2">
                    <div className="text-[11px] text-gray-700" title={t.contract_code}>
                      {instrumentLabel(t.product, t.contract_code)}
                    </div>
                    {dateMeta && <div className="text-[10px] text-gray-400">{dateMeta}</div>}
                  </td>
                  <td className="px-3 py-2"><SideBadge side={t.side} /></td>
                  <td className="px-3 py-2 text-right">{orderPriceLabel(t)}</td>
                  <td className="px-3 py-2 text-right">{fmtPrice(t.size_mwh)}</td>
                  <td className="px-3 py-2"><StatusPill status={t.status} /></td>
                  <td className="px-3 py-2 text-right">{t.filled_mwh > 0 ? fmtPrice(t.filled_mwh) : '—'}</td>
                  <td className="px-3 py-2 text-right">{t.avg_fill_px != null ? fmtPrice(t.avg_fill_px) : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <PageControls count={data?.count ?? 0} limit={data?.limit ?? TABLE_PAGE_SIZE} offset={data?.offset ?? offset} onOffsetChange={setOffset} noun="intents" />
    </>
  )
}

function OldExposureTab({ filters }: { filters: TradesSearch }) {
  const apiFilters = {
    thesis_id: filters.thesis,
    product: ['rdn'],
    contract: filters.contract,
  }
  const { data, isLoading, error } = useExposure(apiFilters)

  if (error) return <Empty message="Couldn't load exposure." />
  if (isLoading) return <Empty message="Loading…" />
  const exposure = data?.exposure ?? []
  return (
    <>
      <ExposureCards groups={exposure} />
      {exposure.length === 0 ? (
        <Empty message="No open exposure — everything is paired or delivered." />
      ) : (
        <ExposureTable groups={exposure} />
      )}
    </>
  )
}

function ExposureCards({ groups }: { groups: ApiExposureGroup[] }) {
  const openMWh = groups.reduce((s, g) => s + Math.abs(g.net_mwh), 0)
  const unrealized = groups.reduce((s, g) => s + (g.unrealized_pnl_pln ?? 0), 0)
  const unbalanced = groups.filter((g) => g.status === 'unbalanced').length
  return <SummaryCards cards={[
    { label: 'Open exposure', value: openMWh.toFixed(2), colorCls: openMWh > 0 ? 'text-amber-700' : 'text-gray-900', unit: 'MWh', highlight: true },
    { label: 'Unrealized P&L', value: unrealized.toFixed(2), colorCls: pnlText(unrealized), unit: 'PLN' },
    { label: 'Unbalanced pairs', value: String(unbalanced), colorCls: unbalanced > 0 ? 'text-red-700' : 'text-gray-900' },
  ]} />
}

function exposureLegLabel(leg: ApiExposureLeg | undefined): string {
  if (!leg) return '—'
  if (leg.avg_price_pln == null) return `${leg.side} awaiting price`
  return `${leg.side} @ ${fmtPrice(leg.avg_price_pln)}`
}

function exposureLeg(group: ApiExposureGroup, suffix: '_F1' | '_F2'): ApiExposureLeg | undefined {
  return group.legs.find((leg) => leg.contract_code.endsWith(suffix))
}

function exposureDirection(group: ApiExposureGroup): string {
  const f1 = exposureLeg(group, '_F1')
  const f2 = exposureLeg(group, '_F2')
  if (f1 && f2) {
    if (f1.side === 'buy' && f2.side === 'sell') return 'LONG'
    if (f1.side === 'sell' && f2.side === 'buy') return 'SHORT'
  }
  if (f1?.side === 'buy') return 'PARTIAL LONG'
  if (f1?.side === 'sell') return 'PARTIAL SHORT'
  if (f2?.side === 'sell') return 'PARTIAL LONG'
  if (f2?.side === 'buy') return 'PARTIAL SHORT'
  return group.legs[0] ? 'PARTIAL' : '—'
}

function exposureGroupContract(group: ApiExposureGroup): string {
  const first = group.legs[0]?.contract_code ?? group.group_id
  const parsed = parseRdnContractCode(first)
  if (parsed) return `${fmtDateShort(parsed.date)}${FIELD_SEPARATOR}${parsed.hour}`
  return first.replace(/_F[12]$/, '')
}

function ExposureTable({ groups }: { groups: ApiExposureGroup[] }) {
  const days = useMemo(() => {
    const byDay = new Map<string, ApiExposureGroup[]>()
    for (const group of groups) {
      const list = byDay.get(group.market_day)
      if (list) list.push(group)
      else byDay.set(group.market_day, [group])
    }
    return [...byDay.entries()]
  }, [groups])
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set())
  const toggleDay = (day: string) =>
    setCollapsedDays((prev) => {
      const next = new Set(prev)
      if (next.has(day)) next.delete(day)
      else next.add(day)
      return next
    })

  const worstStatus = (rows: ApiExposureGroup[]): string => {
    const statuses = rows.map((g) => g.status)
    if (statuses.some((s) => s === 'unbalanced')) return 'unbalanced'
    if (statuses.some((s) => s === 'partially priced')) return 'partially priced'
    return 'intent submitted'
  }

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full text-xs table-fixed">
        <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <tr className="text-left text-gray-500 uppercase text-[10px]">
            <th className="px-3 py-2">Market day / arbitrage pair</th>
            <th className="px-3 py-2 w-32">Direction</th>
            <th className="px-3 py-2 w-36">F1 leg</th>
            <th className="px-3 py-2 w-36">F2 leg</th>
            <th className="px-3 py-2 text-right w-24">Net MWh</th>
            <th className="px-3 py-2 text-right w-28">Unrealized</th>
            <th className="px-3 py-2 w-36">Status</th>
          </tr>
        </thead>
        <tbody>
          {days.map(([day, dayGroups]) => {
            const collapsed = collapsedDays.has(day)
            const dayStatus = worstStatus(dayGroups)
            const dayNet = dayGroups.reduce((s, g) => s + g.net_mwh, 0)
            const dayUnreal = dayGroups.reduce((s, g) => s + (g.unrealized_pnl_pln ?? 0), 0)
            return (
              <Fragment key={day}>
                <tr
                  onClick={() => toggleDay(day)}
                  className={`cursor-pointer border-b border-gray-200 ${dayStatus === 'unbalanced' ? 'bg-red-50/60 hover:bg-red-50' : 'bg-gray-50/60 hover:bg-gray-50'}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                    <span className="inline-flex items-center gap-1 font-semibold">
                      {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                      {fmtDate(day)}
                    </span>
                    <span className="ml-1.5 text-gray-400">
                      ({dayGroups.length} {dayGroups.length === 1 ? 'pair' : 'pairs'})
                    </span>
                  </td>
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2" />
                  <td className={`px-3 py-2 text-right font-semibold ${dayNet > 0 ? 'text-green-700' : dayNet < 0 ? 'text-red-700' : 'text-gray-600'}`}>
                    {dayNet > 0 ? '+' : ''}{fmtPrice(dayNet)}
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${pnlText(dayUnreal)}`}>
                    {formatSignedMoney(dayUnreal)}
                  </td>
                  <td className="px-3 py-2"><PaperStatusBadge status={dayStatus} /></td>
                </tr>
                {!collapsed &&
                  dayGroups.map((group) => {
                    const dateMeta = tradeDateMeta(group)
                    return (
                      <tr
                        key={group.group_id}
                        className={`border-b border-gray-100 ${group.status === 'unbalanced' ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-3 py-2 pl-6">
                          <div className="font-mono text-gray-700">{exposureGroupContract(group)}</div>
                          {dateMeta && <div className="mt-0.5 text-[10px] text-gray-400">{dateMeta}</div>}
                        </td>
                        <td className="px-3 py-2 font-semibold text-gray-700">{exposureDirection(group)}</td>
                        <td className="px-3 py-2 font-mono text-gray-600">{group.product === 'rdn' ? exposureLegLabel(exposureLeg(group, '_F1')) : 'N/A'}</td>
                        <td className="px-3 py-2 font-mono text-gray-600">{group.product === 'rdn' ? exposureLegLabel(exposureLeg(group, '_F2')) : 'N/A'}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${group.net_mwh > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {group.net_mwh > 0 ? '+' : ''}{fmtPrice(group.net_mwh)}
                        </td>
                        <td className={`px-3 py-2 text-right ${group.unrealized_pnl_pln != null ? pnlText(group.unrealized_pnl_pln) : 'text-gray-400'}`}>
                          {group.unrealized_pnl_pln != null ? formatSignedMoney(group.unrealized_pnl_pln) : '—'}
                        </td>
                        <td className="px-3 py-2"><PaperStatusBadge status={group.status} /></td>
                      </tr>
                    )
                  })}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function OldPnLTab({ filters }: { filters: TradesSearch }) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set())
  const apiFilters = {
    thesis_id: filters.thesis,
    product: ['rdn'],
    contract: filters.contract,
    from: filters.from,
    to: filters.to,
  }
  const { data, isLoading, error } = useDailyPnL(apiFilters)
  const rows: ApiDailyPnLRow[] = [...(data?.daily_pnl ?? [])].sort(
    (a, b) => b.day.localeCompare(a.day) || a.thesis_id.localeCompare(b.thesis_id),
  )
  const days = useMemo(() => {
    const byDay = new Map<string, ApiDailyPnLRow[]>()
    for (const row of rows) {
      const list = byDay.get(row.day)
      if (list) list.push(row)
      else byDay.set(row.day, [row])
    }
    return [...byDay.entries()]
  }, [rows])

  if (isLoading) return <Empty message="Loading…" />
  if (error) return <Empty message="Couldn't load strategy P&L." />
  if (rows.length === 0) {
    return <Empty message="No realized P&L yet. Completed paper trade groups will appear here by day." />
  }

  const toggleDay = (day: string) =>
    setExpandedDays((prev) => {
      const next = new Set(prev)
      if (next.has(day)) next.delete(day)
      else next.add(day)
      return next
    })

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full text-xs table-fixed">
        <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <tr className="text-left text-gray-500 uppercase text-[10px]">
            <th className="px-3 py-2">Market day / thesis</th>
            <th className="px-3 py-2 text-right w-32">Daily P&L</th>
            <th className="px-3 py-2 text-right w-36">Cumulative P&L</th>
            <th className="px-3 py-2 text-right w-28">Closed groups</th>
            <th className="px-3 py-2 text-right w-28">Open groups</th>
            <th className="px-3 py-2 w-36">Status</th>
          </tr>
        </thead>
        <tbody>
          {days.map(([day, dayRows]) => {
            const dayCollapsed = !expandedDays.has(day)
            const dayStatus = rollupStatus(dayRows.flatMap((r) => r.groups ?? []))
            const dayPnl = dayRows.reduce((s, r) => s + r.realized_pnl_pln, 0)
            const dayCum = dayRows.reduce((s, r) => s + r.cumulative_pnl_pln, 0)
            const dayClosed = dayRows.reduce((s, r) => s + r.completed_groups, 0)
            const dayOpen = dayRows.reduce((s, r) => s + r.open_groups, 0)
            return (
              <Fragment key={day}>
                <tr
                  onClick={() => toggleDay(day)}
                  className={`cursor-pointer border-b border-gray-200 ${dayStatus === 'unbalanced' ? 'bg-red-50/60 hover:bg-red-50' : 'bg-gray-50/60 hover:bg-gray-50'}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                    <span className="inline-flex items-center gap-1 font-semibold">
                      {dayCollapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                      {fmtDate(day)}
                    </span>
                    <span className="ml-1.5 text-gray-400">
                      ({dayRows.length} {dayRows.length === 1 ? 'thesis' : 'theses'})
                    </span>
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${pnlText(dayPnl)}`}>
                    {formatSignedMoney(dayPnl)}
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${pnlText(dayCum)}`}>
                    {formatSignedMoney(dayCum)}
                  </td>
                  <td className="px-3 py-2 text-right">{dayClosed}</td>
                  <td className="px-3 py-2 text-right">{dayOpen}</td>
                  <td className="px-3 py-2"><PaperStatusBadge status={dayStatus} /></td>
                </tr>
                {!dayCollapsed &&
                  dayRows.map((row) => {
                    const key = `${row.thesis_id}:${row.day}`
                    const expanded = expandedKey === key
                    const thesisStatus = rollupStatus(row.groups ?? [])
                    return (
                      <Fragment key={key}>
                        <tr
                          onClick={() => setExpandedKey(expanded ? null : key)}
                          className={`cursor-pointer border-b border-gray-100 ${thesisStatus === 'unbalanced' ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-3 py-2 pl-6">
                            <span className="inline-flex items-center gap-1">
                              {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </span>
                            <Link
                              to="/theses/$thesisId"
                              params={{ thesisId: row.thesis_id }}
                              onClick={(e) => e.stopPropagation()}
                              className="ml-1 text-slate-700 hover:underline"
                            >
                              {row.thesis_name || row.thesis_id.slice(0, 8)}
                            </Link>
                          </td>
                          <td className={`px-3 py-2 text-right ${pnlText(row.realized_pnl_pln)}`}>
                            {formatSignedMoney(row.realized_pnl_pln)}
                          </td>
                          <td className={`px-3 py-2 text-right ${pnlText(row.cumulative_pnl_pln)}`}>
                            {formatSignedMoney(row.cumulative_pnl_pln)}
                          </td>
                          <td className="px-3 py-2 text-right">{row.completed_groups}</td>
                          <td className="px-3 py-2 text-right">{row.open_groups}</td>
                          <td className="px-3 py-2"><PaperStatusBadge status={thesisStatus} /></td>
                        </tr>
                        {expanded && (
                          <tr className="border-b border-gray-200">
                            <td colSpan={6} className="p-0">
                              <DailyTradeBreakdown groups={row.groups ?? []} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function RunDetailModal({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { data, isLoading } = useScriptRun(runId)
  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">
            Run {runId.slice(0, 8)}
            {data && <span className="ml-3 text-xs text-gray-500">
              market {fmtTime(data.market_ts)}{FIELD_SEPARATOR}exit {data.exit_code ?? 'NULL'}{FIELD_SEPARATOR}{data.thesis_name ?? data.thesis_id.slice(0, 8)}
            </span>}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        {isLoading && <div className="p-6 text-sm text-gray-500">Loading…</div>}
        {data && (
          <div className="flex-1 overflow-auto p-5 space-y-4">
            {data.orders.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase text-gray-500 mb-1">
                  Intents submitted in this slot ({data.orders.length})
                </div>
                <ul className="text-xs space-y-1">
                  {data.orders.map((o) => (
                    <li key={o.id} className="flex gap-2 font-mono">
                      <SideBadge side={o.side} />
                      <span title={o.contract_code}>{instrumentLabel(o.product, o.contract_code)}</span>
                      <span className="text-gray-500">settles via {orderPriceLabel(o)}</span>
                      <span className="text-gray-500">{o.size_mwh} MWh</span>
                      <StatusPill status={o.status} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500 mb-1">stdout</div>
              <pre className="p-3 bg-gray-50 border border-gray-200 rounded text-[11px] whitespace-pre-wrap font-mono overflow-auto max-h-[40vh]">
                {data.stdout || '(empty)'}
              </pre>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500 mb-1">stderr</div>
              <pre className="p-3 bg-red-50 border border-red-200 rounded text-[11px] whitespace-pre-wrap font-mono overflow-auto max-h-[40vh] text-red-800">
                {data.stderr || '(empty)'}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function RunsTab({ filters }: { filters: TradesSearch }) {
  const [openRunId, setOpenRunId] = useState<string | null>(null)
  const resetKey = [
    filters.thesis?.join(',') ?? '',
    filters.from ?? '',
    filters.to ?? '',
    filters.errored_only ? '1' : '0',
  ].join('|')
  const [page, setPage] = useState({ key: resetKey, offset: 0 })
  const offset = page.key === resetKey ? page.offset : 0
  const setOffset = (nextOffset: number) => setPage({ key: resetKey, offset: nextOffset })
  const apiFilters = {
    thesis_id: filters.thesis,
    from: filters.from,
    to: filters.to,
    errored_only: filters.errored_only,
    limit: TABLE_PAGE_SIZE,
    offset,
  }
  const { data, isLoading, error } = useScriptRunsList(apiFilters)
  const runs: ApiScriptRunRow[] = data?.runs ?? []

  if (error) return <Empty message="Couldn't load runs." />
  if (isLoading) return <Empty message="Loading…" />
  if (runs.length === 0) return <Empty message="No runs match your filters." />

  return (
    <>
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
            <tr className="text-left text-gray-500 uppercase text-[10px]">
              <th className="px-3 py-2">Run time</th>
              <th className="px-3 py-2">Market time</th>
              <th className="px-3 py-2">Thesis</th>
              <th className="px-3 py-2">Script</th>
              <th className="px-3 py-2 text-right">Exit</th>
              <th className="px-3 py-2 text-right">Duration</th>
              <th className="px-3 py-2 text-right">stdout</th>
              <th className="px-3 py-2 text-right">stderr</th>
              <th className="px-3 py-2 text-right">Intents</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const dur = r.finished_at
                ? `${Math.round((new Date(r.finished_at).getTime() - new Date(r.started_at).getTime()) / 100) / 10}s`
                : '—'
              const exitColor =
                r.exit_code === 0 ? 'text-green-700' :
                r.exit_code == null ? 'text-gray-500' :
                'text-red-700 font-semibold'
              return (
                <tr
                  key={r.id}
                  onClick={() => setOpenRunId(r.id)}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                    <Link
                      to="/theses/$thesisId/script-runs/$scriptRunId"
                      params={{ thesisId: r.thesis_id, scriptRunId: r.id }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-700 hover:underline"
                    >
                      {fmtTime(r.started_at)}
                    </Link>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtTime(r.market_ts)}</td>
                  <td className="px-3 py-2">
                    <Link
                      to="/theses/$thesisId"
                      params={{ thesisId: r.thesis_id }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-700 hover:underline"
                    >
                      {r.thesis_name || r.thesis_id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    <Link
                      to="/theses/$thesisId/scripts/$scriptId"
                      params={{ thesisId: r.thesis_id, scriptId: r.script_id }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-slate-700 hover:underline"
                    >
                      {r.script_id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className={`px-3 py-2 text-right ${exitColor}`}>{r.exit_code ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{dur}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{r.stdout_len}</td>
                  <td className={`px-3 py-2 text-right ${r.stderr_len > 0 ? 'text-red-700' : 'text-gray-600'}`}>
                    {r.stderr_len}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.order_count > 0
                      ? <span className="font-semibold text-slate-700">{r.order_count}</span>
                      : <span className="text-gray-400">0</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <PageControls count={data?.count ?? 0} limit={data?.limit ?? TABLE_PAGE_SIZE} offset={data?.offset ?? offset} onOffsetChange={setOffset} noun="runs" />
      {openRunId && <RunDetailModal runId={openRunId} onClose={() => setOpenRunId(null)} />}
    </>
  )
}

function Empty({ message }: { message: string }) {
  return <div className="p-8 text-center text-sm text-gray-500">{message}</div>
}

function PageControls({
  count,
  limit,
  offset,
  onOffsetChange,
  noun,
}: {
  count: number
  limit: number
  offset: number
  onOffsetChange: (offset: number) => void
  noun: string
}) {
  if (count <= limit && offset === 0) return null
  const start = count === 0 ? 0 : offset + 1
  const end = Math.min(offset + limit, count)
  const prev = Math.max(0, offset - limit)
  const next = offset + limit
  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-2 text-xs text-gray-500">
      <span>{start}-{end} of {count} {noun}</span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" disabled={offset <= 0} onClick={() => onOffsetChange(prev)} title="Previous page">
          <ChevronLeft className="h-3.5 w-3.5" />
          Prev
        </Button>
        <Button variant="secondary" disabled={next >= count} onClick={() => onOffsetChange(next)} title="Next page">
          Next
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

function DateRange({
  from, to, onChange,
}: { from?: string; to?: string; onChange: (from?: string, to?: string) => void }) {
  const fmt = (iso?: string) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }
  // Empty -> '' (explicit "cleared", persisted) rather than undefined (which would re-apply the default).
  const parse = (v: string): string => {
    if (!v) return ''
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? '' : d.toISOString()
  }
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className="text-gray-500">From</span>
      <input
        type="datetime-local"
        value={fmt(from)}
        onChange={(e) => onChange(parse(e.target.value), to)}
        className="px-2 py-1 text-xs border border-gray-300 rounded-md"
      />
      <span className="text-gray-500">To</span>
      <input
        type="datetime-local"
        value={fmt(to)}
        onChange={(e) => onChange(from, parse(e.target.value))}
        className="px-2 py-1 text-xs border border-gray-300 rounded-md"
      />
    </div>
  )
}

export function TradesPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/app/trades' }) as TradesSearch
  // From defaults to last 30 days. An explicit empty string (user cleared it)
  // means "no bound — fetch whole history"; undefined means "never set".
  const defaultFromValue = useMemo(() => defaultFrom(), [])
  const tab: Tab = normalizeTab(search.tab)

  const filters: TradesSearch = {
    thesis: ensureArray(search.thesis),
    product: ensureArray(search.product),
    status: ensureArray(search.status),
    contract: search.contract,
    from: search.from === undefined ? defaultFromValue : (search.from || undefined),
    to: search.to || undefined,
    errored_only: search.errored_only,
    enabled_only: search.enabled_only,
    graduated_only: search.graduated_only,
    view: search.view,
  }

  const updateSearch = (next: Partial<TradesSearch>) => {
    navigate({
      to: '/trades',
      search: { ...search, ...next } as TradesSearch,
      replace: true,
    })
  }

  const { data: thesesData } = useTheses()
  const thesisOptions = (thesesData?.theses ?? []).map((t) => ({
    value: t.id,
    label: t.name || t.id.slice(0, 8),
  }))

  const dailyPnLFilters = {
    thesis_id: filters.thesis,
    product: ['rdn'],
    contract: filters.contract,
    from: filters.from,
    to: filters.to,
  }
  const { data: dailyPnLData } = useDailyPnL(dailyPnLFilters, { enabled: tab === 'old-pnl' })
  const dailyPnLRows = dailyPnLData?.daily_pnl ?? []
  const showProductFilter = tab === 'positions' || tab === 'executions' || tab === 'pnl'
  const showContractFilter = tab !== 'runs' && tab !== 'scripts'
  const showDateRange = tab === 'executions' || tab === 'runs' || tab === 'old-orders' || tab === 'old-pnl'
  const statusOptions =
    tab === 'positions' || tab === 'pnl'
      ? EXPOSURE_STATUSES.map((s) => ({ value: s, label: s }))
      : tab === 'executions' || tab === 'old-orders'
        ? ORDER_STATUSES.map((s) => ({ value: s, label: s }))
        : []

  return (
    <div className="flex flex-col flex-1 h-full bg-app-bg min-w-0">
      <PageHeader crumbs={[{ label: 'Trades' }]} />

      <Tabs tabs={TRADE_TABS} active={tab} onChange={(t) => updateSearch({ tab: t, status: undefined })} />

      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <MultiSelect
          label="Thesis"
          options={thesisOptions}
          selected={filters.thesis ?? []}
          onChange={(next) => updateSearch({ thesis: next })}
        />
        {showProductFilter && (
          <MultiSelect
            label="Product"
            options={PRODUCTS.map((p) => ({ value: p, label: p.toUpperCase() }))}
            selected={filters.product ?? []}
            onChange={(next) => updateSearch({ product: next })}
          />
        )}
        {statusOptions.length > 0 && (
          <MultiSelect
            label="Status"
            options={statusOptions}
            selected={filters.status ?? []}
            onChange={(next) => updateSearch({ status: next })}
          />
        )}
        {showContractFilter && (
          <input
            type="text"
            value={filters.contract ?? ''}
            onChange={(e) => updateSearch({ contract: e.target.value || undefined })}
            placeholder="Contract code"
            className="px-3 py-1.5 text-xs border border-gray-300 rounded-md w-44"
          />
        )}
        {tab === 'runs' && (
          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={!!filters.errored_only}
              onChange={(e) => updateSearch({ errored_only: e.target.checked || undefined })}
            />
            Errored only
          </label>
        )}
        {tab === 'scripts' && (
          <>
            <label className="flex items-center gap-1.5 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={!!filters.enabled_only}
                onChange={(e) => updateSearch({ enabled_only: e.target.checked || undefined })}
              />
              Enabled only
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={!!filters.graduated_only}
                onChange={(e) => updateSearch({ graduated_only: e.target.checked || undefined })}
              />
              Graduated only
            </label>
          </>
        )}
        {showDateRange && (
          <DateRange
            from={filters.from}
            to={filters.to}
            onChange={(from, to) => updateSearch({ from, to })}
          />
        )}
        {(filters.thesis?.length || filters.product?.length || filters.status?.length ||
          filters.contract || filters.errored_only || filters.enabled_only || filters.graduated_only) ? (
          <button
            onClick={() =>
              navigate({ to: '/trades', search: { tab } as TradesSearch, replace: true })
            }
            className="text-xs text-gray-500 hover:text-gray-800 px-2 py-1.5"
          >
            Clear filters
          </button>
        ) : null}
      </div>

      {tab === 'old-pnl' && <DailyPnLCards rows={dailyPnLRows} />}

      {tab === 'positions'    && <PositionsTab    filters={filters} />}
      {tab === 'executions'   && <ExecutionsTab   filters={filters} />}
      {tab === 'pnl'          && <PnLTab          filters={filters} />}
      {tab === 'runs'         && <RunsTab         filters={filters} />}
      {tab === 'scripts'      && <ScriptsTab      filters={filters} thesisOptions={thesisOptions} />}
      {tab === 'old-orders'   && <OldOrdersTab    filters={filters} />}
      {tab === 'old-exposure' && <OldExposureTab  filters={filters} />}
      {tab === 'old-pnl'      && <OldPnLTab       filters={filters} />}
    </div>
  )
}

function ScriptsTab({
  filters,
  thesisOptions,
}: {
  filters: TradesSearch
  thesisOptions: { value: string; label: string }[]
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { data, isLoading, error } = useTraderScripts()
  const [viewId, setViewId] = useState<string | null>(filters.view ?? null)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (filters.view && filters.view !== viewId) setViewId(filters.view)
  }, [filters.view, viewId])

  const closeView = () => {
    setViewId(null)
    if (filters.view) {
      const next: TradesSearch = { ...filters, tab: 'scripts' }
      delete next.view
      navigate({
        to: '/trades',
        search: next as TradesSearch,
        replace: true,
      })
    }
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['trader-scripts'] })

  const scripts = useMemo(() => {
    const thesisFilter = filters.thesis ?? []
    return (data ?? [])
      .filter((s) => thesisFilter.length === 0 || thesisFilter.includes(s.thesis_id))
      .filter((s) => !filters.enabled_only || s.enabled)
      .filter((s) => !filters.graduated_only || s.thesis_status === 'graduated')
      .sort(
        (a, b) =>
          Number(b.enabled) - Number(a.enabled) ||
          b.created_at.localeCompare(a.created_at),
      )
  }, [data, filters.thesis, filters.enabled_only, filters.graduated_only])

  const { data: pnlData } = useDailyPnL({})
  const pnlByThesis = useMemo(() => {
    const m = new Map<string, number>()
    for (const row of pnlData?.daily_pnl ?? []) {
      m.set(row.thesis_id, (m.get(row.thesis_id) ?? 0) + row.realized_pnl_pln)
    }
    return m
  }, [pnlData])

  const onToggle = async (s: ApiTraderScript) => {
    const otherEnabled = (data ?? []).filter(
      (x) => x.thesis_id === s.thesis_id && x.id !== s.id && x.enabled,
    ).length
    const msg = s.enabled
      ? `Disable script ${s.id.slice(0, 8)} for "${s.thesis_name}"? It will stop trading.`
      : otherEnabled > 0
        ? `Enable script ${s.id.slice(0, 8)} for "${s.thesis_name}"? ${otherEnabled} other enabled script${otherEnabled > 1 ? 's' : ''} for this thesis will be disabled — only one script can run per thesis.`
        : `Enable script ${s.id.slice(0, 8)} for "${s.thesis_name}"? It will start trading.`
    if (!confirm(msg)) return
    setBusyId(s.id)
    try {
      await patchTraderScript(s.id, { enabled: !s.enabled })
      invalidate()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Toggle failed')
    } finally {
      setBusyId(null)
    }
  }

  const onDelete = async (s: ApiTraderScript) => {
    if (!confirm(`Delete script for "${s.thesis_name}"? The thesis will be flagged to regenerate a new one.`)) return
    setBusyId(s.id)
    try {
      await deleteTraderScript(s.id)
      invalidate()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusyId(null)
    }
  }

  if (error) return <Empty message="Couldn't load scripts." />
  if (isLoading) return <Empty message="Loading…" />

  return (
    <>
      <div className="flex justify-start px-4 py-2 bg-white border-b border-gray-200">
        <button
          onClick={() => setUploadOpen(true)}
          className="px-3 py-1.5 text-xs font-medium text-white bg-slate-800 hover:bg-slate-900 rounded-md"
        >
          Upload script
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        {scripts.length === 0 ? (
          <Empty message="No scripts." />
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
              <tr className="text-left text-gray-500 uppercase text-[10px]">
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Thesis</th>
                <th className="px-3 py-2">Script ID</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Cumulative P&L</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scripts.map((s) => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtTime(s.created_at)}</td>
                  <td className="px-3 py-2">
                    <Link
                      to="/theses/$thesisId"
                      params={{ thesisId: s.thesis_id }}
                      className="text-slate-700 hover:underline"
                    >
                      {s.thesis_name || s.thesis_id.slice(0, 8)}
                    </Link>
                    <span className={`ml-2 inline-block px-2 py-0.5 text-[10px] rounded-full border font-medium uppercase ${
                      s.thesis_status === 'graduated'
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : s.thesis_status === 'killed'
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : 'bg-slate-50 text-slate-700 border-slate-200'
                    }`}>
                      {s.thesis_status}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    <Link
                      to="/theses/$thesisId/scripts/$scriptId"
                      params={{ thesisId: s.thesis_id, scriptId: s.id }}
                      className="text-slate-700 hover:underline"
                    >
                      {s.id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 text-[10px] rounded-full border font-medium uppercase ${
                      s.enabled
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-gray-50 text-gray-600 border-gray-200'
                    }`}>
                      {s.enabled ? 'enabled' : 'disabled'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {pnlByThesis.has(s.thesis_id) ? (
                      <span className={pnlText(pnlByThesis.get(s.thesis_id)!)}>
                        {formatSignedMoney(pnlByThesis.get(s.thesis_id)!)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="secondary"
                        disabled={busyId === s.id}
                        onClick={() => onToggle(s)}
                        className={s.enabled ? '!text-red-600' : ''}
                      >
                        {s.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button variant="secondary" onClick={() => setViewId(s.id)}>View</Button>
                      <Button variant="secondary" onClick={() => setEditId(s.id)}>Edit</Button>
                      <Button
                        variant="secondary"
                        disabled={busyId === s.id}
                        onClick={() => onDelete(s)}
                        className="!text-red-600"
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {viewId && (
        <ScriptViewModal scriptId={viewId} onClose={closeView} />
      )}
      {editId && (
        <ScriptEditModal
          scriptId={editId}
          onClose={() => setEditId(null)}
          onSaved={() => { setEditId(null); invalidate() }}
        />
      )}
      {uploadOpen && (
        <ScriptUploadModal
          thesisOptions={thesisOptions}
          onClose={() => setUploadOpen(false)}
          onCreated={() => { setUploadOpen(false); invalidate() }}
        />
      )}
    </>
  )
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-xl w-[min(960px,90vw)] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">{children}</div>
      </div>
    </div>
  )
}

function ScriptViewModal({ scriptId, onClose }: { scriptId: string; onClose: () => void }) {
  const { data, isLoading } = useTraderScript(scriptId)
  return (
    <ModalShell title={data ? `Script — ${data.thesis_name}` : 'Script'} onClose={onClose}>
      {isLoading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <pre className="font-mono text-[11px] whitespace-pre-wrap break-words text-gray-800">
          {data?.script ?? ''}
        </pre>
      )}
    </ModalShell>
  )
}

function ScriptEditModal({
  scriptId,
  onClose,
  onSaved,
}: {
  scriptId: string
  onClose: () => void
  onSaved: () => void
}) {
  const { data, isLoading } = useTraderScript(scriptId)
  const [text, setText] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const value = text ?? data?.script ?? ''

  const onSave = async () => {
    if (!text || text.trim() === '') return
    setSaving(true)
    try {
      await patchTraderScript(scriptId, { script: text })
      onSaved()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalShell title={data ? `Edit — ${data.thesis_name}` : 'Edit script'} onClose={onClose}>
      {isLoading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : (
        <div className="flex flex-col gap-3">
          <textarea
            value={value}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            className="w-full h-[55vh] font-mono text-[11px] border border-gray-300 rounded-md p-2"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              disabled={saving || text == null || text.trim() === ''}
              onClick={onSave}
              className="px-3 py-1.5 text-xs font-medium text-white bg-slate-800 hover:bg-slate-900 rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  )
}

function ScriptUploadModal({
  thesisOptions,
  onClose,
  onCreated,
}: {
  thesisOptions: { value: string; label: string }[]
  onClose: () => void
  onCreated: () => void
}) {
  const [thesisId, setThesisId] = useState('')
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)

  const onUpload = async () => {
    if (!thesisId || text.trim() === '') return
    setSaving(true)
    try {
      await createTraderScript(thesisId, text)
      onCreated()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalShell title="Upload trader script" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <label className="text-xs text-gray-700 flex flex-col gap-1">
          Thesis
          <select
            value={thesisId}
            onChange={(e) => setThesisId(e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-300 rounded-md"
          >
            <option value="">Select…</option>
            {thesisOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-gray-700 flex flex-col gap-1">
          Script (Python)
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the trader_sdk-based python script here…"
            spellCheck={false}
            className="w-full h-[50vh] font-mono text-[11px] border border-gray-300 rounded-md p-2"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={saving || !thesisId || text.trim() === ''}
            onClick={onUpload}
            className="px-3 py-1.5 text-xs font-medium text-white bg-slate-800 hover:bg-slate-900 rounded-md disabled:opacity-50"
          >
            {saving ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </ModalShell>
  )
}
